'use client';

import { useRouter } from 'next/navigation';
import { useEffect } from 'react';
import { fetchCitySyncStatus } from '../_data/attractions.api';

/** How often the listing asks whether the city has finished loading. */
const POLL_MS = 8_000;
/** Stop asking after this long; a refresh by hand still works. */
const GIVE_UP_MS = 10 * 60_000;

interface CityLoadingNoticeProps {
  cityId: number;
  cityName: string;
  /** Results on screen now. */
  shown: number;
  /** Bridgify's count for the city, when known. */
  expectedTotal: number | null;
}

/**
 * Shown while a city's FIRST read from Bridgify is still running — a big city
 * (more than ~900 products) is answered with its first page and completed in
 * the background (on-demand catalogue, 2026-10-26). The results and their sort
 * cover only what has arrived, so this says so, polls the service, and
 * refreshes the page once the city is complete.
 */
export function CityLoadingNotice({ cityId, cityName, shown, expectedTotal }: CityLoadingNoticeProps) {
  const router = useRouter();

  useEffect(() => {
    const started = Date.now();
    let cancelled = false;
    const timer = setInterval(async () => {
      if (Date.now() - started > GIVE_UP_MS) {
        clearInterval(timer);
        return;
      }
      const status = await fetchCitySyncStatus(cityId);
      if (cancelled || !status || status.state === 'loading') return;
      clearInterval(timer);
      router.refresh();
    }, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [cityId, router]);

  const place = cityName ? ` in ${cityName}` : '';
  return (
    <div className="atr-city-loading" role="status" aria-live="polite">
      <span className="atr-city-loading__spinner" aria-hidden="true" />
      <span>
        {shown > 0
          ? `Showing the first ${shown.toLocaleString()}${expectedTotal ? ` of about ${expectedTotal.toLocaleString()}` : ''} attractions${place}. We're loading the rest — this page will update automatically.`
          : `Loading attractions${place}… this page will update automatically.`}
      </span>
    </div>
  );
}
